import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useState, useRef, useEffect, useMemo, useId } from 'react';
import clsx from 'clsx';
import { ButtonPrimitive } from '../button-primitive/button-primitive.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { LinkPrimitive } from '../link-primitive/link-primitive.js';
import { useFlag } from '../settings/settings.flags.js';
import { handleCardContextMenu, handleCardClick } from './card-primitive.utils.js';
import { root, main, interactive, button, link } from './card-primitive.css.js';

const CardPrimitiveContext = createContext(null);
const IRoot = forwardRef(({ as = 'div', className, autoInteractive: autoInteractiveIn, ...props }, ref) => {
    // #TODO: when cleaning up this flag, remember to document new behavior
    const cardAutoInteractiveFlag = useFlag('cardAutoInteractive');
    // Require the prop to be set AND the flag to be enabled to access autoInteractive behavior
    const autoInteractive = !!(autoInteractiveIn && cardAutoInteractiveFlag);
    const [titleElRef, registerTitleElRef] = useState(null);
    const [labeledById, setLabeledById] = useState(undefined);
    const mainButtonRef = useRef(null);
    useEffect(() => {
        if (titleElRef) {
            setLabeledById(titleElRef.id);
        }
    }, [titleElRef]);
    const handleClick = (e) => {
        if (autoInteractive) {
            handleCardClick(e, mainButtonRef);
        }
    };
    const handleContextMenu = (e) => {
        if (autoInteractive) {
            handleCardContextMenu(e, mainButtonRef);
        }
    };
    const value = useMemo(() => ({ labeledById, registerTitleElRef, autoInteractive, mainButtonRef }), [labeledById, autoInteractive, mainButtonRef]);
    const Tag = as;
    return (jsx(CardPrimitiveContext.Provider, { value: value, children: jsx(Tag, { "data-fpl-component": "primitive", "data-card-auto-interactive": autoInteractive || undefined, className: clsx(root, className), ref: ref, onClick: autoInteractive ? handleClick : undefined, onContextMenu: autoInteractive ? handleContextMenu : undefined, ...props }) }));
});
IRoot.displayName = 'CardPrimitive.Root';
/**
 * The root container of the CardPrimitive component. A card is an accessible pattern allowing
 * for visually nesting interactive elements while keeping them separate for screen readers.
 *
 * If clicking the card itself is the only way to take action, use Card.Link or Card.Button inside Card.Root:
 * ```
 * <Card.Root>
 *   <Card.Link href="#" />
 *   {card contents}
 *   <Card.Title>
 *     <h2>This text labels the Card.Link</h2>
 *   </Card.Title>
 * </Card.Root>
 * ```
 *
 * If you want to extend the clickable area of a particular element in the card to the entire card,
 * wrap that element with Card.Main:
 * ```
 * <Card.Root>
 *   <Card.Main>
 *     <a href="#">Go to file</a>
 *   </Card.Main>
 *   {card contents}
 * </Card.Root>
 * ```
 *
 * @see https://fpl.figma.design/primitives/card/
 */
const Root = forwardRef((props, ref) => (jsx(IRoot, { as: "div", ...props, ref: ref })));
Root.displayName = 'CardPrimitive.Root';
/**
 * An alternative root container of the CardPrimitive component when a tr element is needed.
 *
 * Ensure that immediate children of this component are semantically valid elements (table cells)
 * ```
 * <Card.TableRowRoot>
 *   <td>
 *      <Card.Link href="#" />
 *   </td>
 *   <td>
 *     <Card.Title>
 *       <h2>This text labels the Card.Link</h2>
 *     </Card.Title>
 *   </td>
 * </Card.TableRowRoot>
 * ```
 *
 * @see https://fpl.figma.design/primitives/card/
 */
const TableRowRoot = forwardRef((props, ref) => (jsx(IRoot, { as: "tr", ...props, ref: ref })));
TableRowRoot.displayName = 'CardPrimitive.TableRowRoot';
/**
 * Clicking anywhere on the card will trigger a click on CardPrimitive.Main's children, except
 * when clicking on elements wrapped in CardPrimitive.Interactive or elements that are automatically
 * detected as interactive (when autoInteractive is enabled on Root).
 */
const Main = forwardRef(({ className, ...props }, ref) => (jsx("div", { "data-fpl-component": "primitive", className: clsx(main, className), ref: ref, ...props })));
Main.displayName = 'CardPrimitive.Main';
/**
 * Use inside CardPrimitive.Root to wrap elements that should not trigger the
 * CardPrimitive.MainButton or CardPrimitive.MainLink when clicked. Elements inside of
 * CardPrimitive.Interactive sit on top of the card's main clickable area and preserve
 * the card's hover state.
 *
 * When autoInteractive is enabled on Root, common interactive elements (buttons, links, inputs)
 * are automatically treated as interactive without needing this wrapper.
 */
const Interactive = forwardRef(({ className, ...props }, ref) => (jsx("div", { "data-fpl-component": "primitive", className: clsx(interactive, className), ref: ref, ...props })));
Interactive.displayName = 'CardPrimitive.Interactive';
/**
 * Use inside CardPrimitive.Root to make the entire card a button. Clicking anywhere on
 * the card will trigger a click on this element, unless the click's propagation is
 * stopped or it's inside of CardPrimitive.Interactive. Set the title via `aria-label`
 * or wrap the text to be used as the title inside the card with CardPrimitive.Title.
 */
const MainButton = forwardRef(({ className, ...props }, ref) => {
    const { labeledById, mainButtonRef } = useValidatedContext(CardPrimitiveContext, 'CardPrimitive.Main', 'CardPrimitive.Root');
    const mergedRef = useMergeRefs(ref, mainButtonRef);
    return (jsx(ButtonPrimitive, { "aria-labelledby": labeledById, className: clsx(button, className), "data-card-main-action": true, ref: mergedRef, ...props, children: null }));
});
MainButton.displayName = 'CardPrimitive.MainButton';
/**
 * Use inside CardPrimitive.Root to make the entire card a link. Clicking anywhere on
 * the card will trigger a click on this element, unless the click's propagation is
 * stopped or it's inside of CardPrimitive.Interactive. Set the title via `aria-label`
 * or wrap the text to be used as the title inside the card with CardPrimitive.Title.
 * */
const MainLink = forwardRef(({ className, ...props }, ref) => {
    const { labeledById, mainButtonRef } = useValidatedContext(CardPrimitiveContext, 'CardPrimitive.Main', 'CardPrimitive.Root');
    const mergedRef = useMergeRefs(ref, mainButtonRef);
    return (jsx(LinkPrimitive, { "aria-labelledby": labeledById, className: clsx(link, className), "data-card-main-action": true, ref: mergedRef, ...props, children: null }));
});
MainLink.displayName = 'CardPrimitive.MainLink';
/**
 * When using CardPrimitive.MainLink or CardPrimitive.MainButton, wrap text that should be used
 * as the title of the card with CardPrimitive.Title. The text contents of the children
 * will override aria-label on CardPrimitive.MainLink or CardPrimitive.MainButton. It wraps
 * its contents in aria-hidden="true" to prevent the text from being read out twice by
 * screen readers.
 */
const Title = forwardRef(({ as, ...props }, refIn) => {
    const Component = (as ?? 'div');
    const { registerTitleElRef } = useValidatedContext(CardPrimitiveContext, 'CardPrimitive.Main', 'CardPrimitive.Root');
    const generatedId = useId();
    const ref = useMergeRefs(refIn, registerTitleElRef);
    return (jsx(Component, { "data-fpl-component": "primitive", "aria-hidden": "true", id: props.id ?? generatedId, ref: ref, ...props }));
});
Title.displayName = 'CardPrimitive.Title';

export { Interactive, Main, MainButton, MainLink, Root, TableRowRoot, Title };
//# sourceMappingURL=card-primitive.js.map
